// src/lib/firstWeek.ts
//
// "Your first week" — a fresh household lands on an empty Today with no
// guidance. Four real steps, each a link into the flow that actually does
// the thing, each collapsing to a one-line result once done. The card hides
// itself the moment fewer than two steps remain, and stays hidden for a week
// after "Hide for now" — never a permanent dismiss, never a badge/count.

import { supabase } from '@/lib/supabase'

/** The page step's ?plan=paper, or ?plan=sample to load the bundled sample.
 *  Any other ?plan= belongs to someone else (Week's ?plan=1, planning by hand). */
export function paperPlanRequest(plan: string | null): 'paper' | 'sample' | null {
  return plan === 'paper' || plan === 'sample' ? plan : null
}

export type FirstWeekStepId = 'plan-year' | 'people' | 'page' | 'partner' | 'routine'

export interface FirstWeekSignals {
  /** A `planning_sessions` row for the current calendar year (`horizon =
   *  'annual'`, `period_token = String(currentYear)`) has been saved —
   *  `notes.savedAt` is set — by anyone in the household. */
  yearPlanned: boolean
  /** family_members rows visible to this account. */
  memberCount: number
  /** A page has been committed at least once — an `attachments` row whose
   *  `storage_path` matches the page-capture path (`<uid>/page/<uuid>.ext`),
   *  written by `useCommitPage` for every committed page, sample or real. */
  pageCommitted: boolean
  /** A second active `household_members` row, or an unexpired invitation. */
  partnerInvited: boolean
  /** `routines` rows visible to this account. */
  routineCount: number
}

export interface FirstWeekStep {
  id: FirstWeekStepId
  title: string
  done: boolean
  doneLine: string | null
  to: string
  /** A short muted line under an undone step's label — currently only the
   *  year step carries one. */
  hint?: string
}

export function firstWeekSteps(s: FirstWeekSignals): FirstWeekStep[] {
  return [
    {
      id: 'plan-year',
      title: 'Plan your year',
      done: s.yearPlanned,
      doneLine: s.yearPlanned ? 'see Year' : null,
      to: '/year',
      hint: 'One thing you want to be true by December.',
    },
    {
      id: 'people',
      title: 'Name your people',
      done: s.memberCount > 1,
      doneLine: s.memberCount > 1 ? `${s.memberCount} people` : null,
      to: '/settings#household',
    },
    {
      id: 'page',
      title: "Snap this week's page",
      done: s.pageCommitted,
      doneLine: s.pageCommitted ? 'see This Week' : null,
      to: '/today?plan=paper',
    },
    {
      id: 'partner',
      title: 'Invite your partner',
      done: s.partnerInvited,
      doneLine: s.partnerInvited ? 'invited' : null,
      to: '/settings#invite',
    },
    {
      id: 'routine',
      title: 'Add one routine',
      done: s.routineCount > 0,
      doneLine: s.routineCount > 0 ? 'see Routines' : null,
      to: '/routines',
    },
  ]
}

/** Dismissal persists. Help may explicitly reopen the invitation. */
export function shouldShowFirstWeek(steps: FirstWeekStep[], hiddenAt: string | null, _now: Date): boolean {
  return !hiddenAt && steps.filter(step => !step.done).length >= 2
}

export const FIRST_WEEK_HIDE_KEY = (uid: string) => `symphony.firstWeek.hidden.${uid}`

/**
 * Is Getting Started open right now?
 *
 * Two quite different reasons, and keeping them apart is the point:
 *
 *   ASKED FOR — somebody chose "Getting started" from the menu. That works
 *   whatever the planner holds, whether it was dismissed before, and whether
 *   this is a first week or a thousandth. Onboarding you cannot re-open is
 *   not independent of the planner; it is a function of how empty it is.
 *
 *   OFFERED — a genuinely empty planner, with steps left and no dismissal.
 *   This is the nudge, and it obeys every one of those conditions.
 *
 * Extracted from HomeViewContainer so the independence is a property with a
 * name and a test, rather than a boolean expression in a render (Codex,
 * 2026-09-24: "Remaining scope also includes independent Getting Started
 * access").
 */
export function shouldOpenFirstWeek(input: {
  /** ?welcome=1 — an explicit request. */
  asked: boolean
  /** How much the planner holds. Only consulted for the OFFER. */
  taskCount: number
  steps: FirstWeekStep[]
  hiddenAt: string | null
  now: Date
}): boolean {
  if (input.asked) return true
  return input.taskCount === 0 && shouldShowFirstWeek(input.steps, input.hiddenAt, input.now)
}

// ---------------------------------------------------------------------------
// Sample page — "no paper handy?" commits the bundled sample image through
// the exact same paper flow as a real page. The rows it creates are real
// rows (so the step genuinely completes), but they're fake data, so a
// household that later snaps a real page should be able to clear them out.
// The row ids are tracked here, client-side, rather than as a DB column —
// `useCommitPage` has no `capture_meta` column to write a marker into.

export const FIRST_WEEK_SAMPLE_KEY = (uid: string) => `symphony.firstWeek.sampleIds.${uid}`

export interface SampleIds {
  taskIds: string[]
  noteIds: string[]
}

const EMPTY_SAMPLE_IDS: SampleIds = { taskIds: [], noteIds: [] }

export function readSampleIds(uid: string): SampleIds {
  try {
    const raw = localStorage.getItem(FIRST_WEEK_SAMPLE_KEY(uid))
    if (!raw) return EMPTY_SAMPLE_IDS
    const parsed = JSON.parse(raw) as Partial<SampleIds>
    return {
      taskIds: Array.isArray(parsed.taskIds) ? parsed.taskIds : [],
      noteIds: Array.isArray(parsed.noteIds) ? parsed.noteIds : [],
    }
  } catch {
    return EMPTY_SAMPLE_IDS
  }
}

export function writeSampleIds(uid: string, ids: SampleIds): void {
  try {
    localStorage.setItem(FIRST_WEEK_SAMPLE_KEY(uid), JSON.stringify(ids))
  } catch {
    // ignore — sample tracking is a nice-to-have, not load-bearing
  }
}

export function clearSampleIdsRecord(uid: string): void {
  try {
    localStorage.removeItem(FIRST_WEEK_SAMPLE_KEY(uid))
  } catch {
    // ignore
  }
}

export function hasSampleIds(uid: string): boolean {
  const ids = readSampleIds(uid)
  return ids.taskIds.length > 0 || ids.noteIds.length > 0
}

/**
 * Delete the rows the bundled sample page created, by id. Owner deletes are
 * what RLS already allows, so this goes straight at the tables rather than
 * through the task/note hooks — those resolve on the optimistic update and
 * say nothing about whether the write landed.
 *
 * Returns true only when BOTH deletes came back clean. The localStorage id
 * record is the ONLY thing pointing at these rows: clearing it after a
 * partial delete strands fake data in a real household with nothing left to
 * find it by, so the caller must keep the record on false.
 */
export async function deleteSampleRows({ taskIds, noteIds }: SampleIds): Promise<boolean> {
  const [tasks, notes] = await Promise.all([
    taskIds.length
      ? supabase.from('tasks').delete().in('id', taskIds)
      : Promise.resolve({ error: null }),
    noteIds.length
      ? supabase.from('notes').delete().in('id', noteIds)
      : Promise.resolve({ error: null }),
  ])
  return !tasks.error && !notes.error
}
